/** Escape one CSV field (RFC 4180). */
function escapeCsvCell(value) {
  const s = value === undefined || value === null ? "" : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Convert array of objects to CSV text.
 * Headers default to keys of the first row; pass headerOrder to force column order
 * (needed when keys look like integers — JS sorts those before other keys).
 */
export function rowsToCsv(rows, headerOrder) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const headers =
    Array.isArray(headerOrder) && headerOrder.length > 0
      ? headerOrder
      : Object.keys(rows[0]);
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) =>
      headers.map((h) => escapeCsvCell(row[h])).join(","),
    ),
  ];
  return lines.join("\r\n");
}

/**
 * Parse CSV text into array of objects (header row → keys).
 * Values are always strings.
 */
export function csvToRows(text) {
  const input = String(text ?? "").replace(/^\uFEFF/, "");
  if (!input.trim()) return [];

  const table = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    const next = input[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      cell = "";
      table.push(row);
      row = [];
    } else if (ch === "\r") {
      row.push(cell);
      cell = "";
      table.push(row);
      row = [];
      if (next === "\n") i += 1;
    } else {
      cell += ch;
    }
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    table.push(row);
  }

  while (table.length > 0) {
    const last = table[table.length - 1];
    if (last.length === 1 && String(last[0]).trim() === "") table.pop();
    else break;
  }

  if (table.length === 0) return [];

  const headers = table[0].map((h) => String(h).trim());
  const objects = [];

  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r];
    const isBlank = cells.every((c) => String(c ?? "").trim() === "");
    if (isBlank) continue;

    const obj = {};
    for (let c = 0; c < headers.length; c += 1) {
      const key = headers[c];
      if (!key) continue;
      obj[key] = cells[c] !== undefined ? String(cells[c]) : "";
    }
    objects.push(obj);
  }

  return objects;
}

/** Trigger a browser download of rows as a .csv file. */
export function downloadCsv(filename, rows, headerOrder) {
  const name = String(filename || "download.csv");
  const finalName = name.toLowerCase().endsWith(".csv") ? name : `${name}.csv`;
  const blob = new Blob([rowsToCsv(rows, headerOrder)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = finalName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
